import { type FC, useEffect, useMemo, useState } from "react";

import { splitSummaryBySubmissions } from "./helpers";
import { ChangeSummary } from "./summary/ChangeSummary";

import { serverGetPublicationSummary } from "api/api-server-publishing";
import { type PublicationSummary } from "api/api-types-publishing";
import { PublishConfirmationDialog } from "components/materials/section/publication/PublishConfirmationDialog";
import { PageList } from "components/materials/section/publication/summary/PageList";
import { PublishingCard } from "components/materials/section/publication/summary/PublishingCard";
import { PublicationType } from "components/server-types";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { IconText } from "mds/components/IconText";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import { ArrowUpToLineIcon, LockLockedIcon } from "mds/icons";
import { useAppSelector } from "store/index";

interface PublicationReviewProps {
  latestPublication: PublicationType;
}

export const PublicationReview: FC<PublicationReviewProps> = ({ latestPublication }) => {
  const [summary, setSummary] = useState<PublicationSummary | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const storePages = useAppSelector((state) => state.pages);

  useEffect(() => {
    serverGetPublicationSummary(latestPublication.id).then(setSummary);
  }, [latestPublication.id]);

  const { filteredSummary, pagesWithSubmissions } = useMemo(() => {
    if (!summary) return { filteredSummary: null, pagesWithSubmissions: [] };
    return splitSummaryBySubmissions(summary, storePages);
  }, [summary, storePages]);

  return (
    <>
      <Card
        className="flex flex-col gap-6 rounded-[12px] p-6 text-black-tint-20 supports-squircle:rounded-[24px] supports-squircle:corner-squircle"
        kind="gray"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3>{t("publishing.review_title")}</h3>
            {summary && (
              <div className="body-s">
                {t("publishing.edits_by_authors", {
                  authors: summary.authors.join(", ") || "-",
                })}
              </div>
            )}
          </div>
          <RoundedButton kind="success" onClick={() => setIsConfirmOpen(true)}>
            <IconText iconStart={<ArrowUpToLineIcon />} text={t("publishing.publish_changes")} />
          </RoundedButton>
        </div>

        <HorizontalDivider />

        {pagesWithSubmissions.length > 0 && (
          <PublishingCard>
            <IconText
              className="h3 mb-1 text-blue-shade-20"
              iconStart={<LockLockedIcon className="text-blue" />}
              text={t("publishing.student_submission_card.title")}
            />
            <div className="body-s mb-4 text-black-tint-20">
              {t("publishing.student_submission_card.description_temp")}
            </div>
            <PageList
              pages={pagesWithSubmissions}
              sectionId={latestPublication.section_id}
              showStatusChip={false}
              showViewPageHistory={false}
            />
          </PublishingCard>
        )}

        {filteredSummary && (
          <ChangeSummary sectionId={latestPublication.section_id} summary={filteredSummary} />
        )}
      </Card>

      <PublishConfirmationDialog
        open={isConfirmOpen}
        publicationId={latestPublication.id}
        onClose={() => setIsConfirmOpen(false)}
      />
    </>
  );
};
